/**
 * Bildirim olayları + push aboneliği eşitlemesi + `?notifications=1` (push tıklaması) işleme.
 * Kabuk başına bir kez (başlıktaki zil) çağrılır.
 */
import { useEffect } from 'react';
import { useSearchParams } from 'react-router';
import { useAuthStore } from '@/entities/session/model/authStore';
import { impersonation } from '@/entities/session/lib/impersonation';
import { usePrefsStore } from '@/entities/preferences/model/prefsStore';
import { eventBus } from '@/shared/realtime/eventBus';
import { toast } from '@/shared/ui/toastStore';
import { disablePush, enablePush } from '@/shared/lib/push/pushClient';
import { useNotificationStore } from './notificationStore';
import type { AppNotification } from './types';

export function useNotificationsRealtime() {
  const authed = useAuthStore((s) => s.status === 'authenticated');
  const userId = useAuthStore((s) => s.user?.userId);
  const desktop = usePrefsStore((s) => s.prefs.desktopNotifications);
  const [params, setParams] = useSearchParams();

  useEffect(() => {
    if (!authed) return;
    const s = useNotificationStore.getState;
    const onNotification = (raw: unknown) => {
      const n = raw as AppNotification;
      s().apply(n);
      // Zil açıkken liste zaten görünüyor; değilse kısa bir bildirim.
      if (!s().open) {
        const tone = n.notificationType === 'error' ? 'warning' : n.notificationType === 'success' ? 'success' : 'info';
        const from = n.senderName ? `${n.senderName}${n.body ? ' · ' : ''}` : '';
        toast[tone](n.title, from + (n.body ?? '') || undefined);
      }
    };
    const onConnection = (state: string) => {
      if (state !== 'connected') return;
      if (s().loaded) void s().load();
      else void s().refreshCount();
    };
    eventBus.on('NotificationReceived', onNotification);
    eventBus.on('ConnectionState', onConnection);
    void s().refreshCount();
    return () => {
      eventBus.off('NotificationReceived', onNotification);
      eventBus.off('ConnectionState', onConnection);
    };
  }, [authed, userId]);

  // Push: izin verilmişse abonelik bu kullanıcıya yazılır; tercih kapatıldıysa bırakılır.
  // Taklit sekmesi abone olmaz — bildirimler gerçek kullanıcının cihazına gitmeli.
  useEffect(() => {
    if (!authed || impersonation.isTab()) return;
    if (desktop) void enablePush(false);
    else void disablePush(true);
  }, [authed, userId, desktop]);

  const flag = params.get('notifications');
  useEffect(() => {
    if (!flag || !authed) return;
    useNotificationStore.getState().setOpen(true);
    const next = new URLSearchParams(params);
    next.delete('notifications');
    setParams(next, { replace: true });
  }, [flag, authed, params, setParams]);
}
